import { Box, Button, Paragraph } from '@deckee/deck-hand';
import React, { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { captureEvent } from '../../../../services/event-capture';
import { AppState } from '../../../../services/store';
import {
  hideAllLayers,
  hideSearchButton,
  moveMapToLocation,
  postMemberSettings,
  selectCurrentMapCenter,
  showSelectMapPoint,
} from '../../../../services/store/modules/app-controls';
import { formatLatLon } from '../../../helpers/coordinate-helpers';
import useIsInBounds from '../../../hooks/use-is-in-bounds';
import { useRadioRangeLayers } from '../../../hooks/use-radio-range-layers';
import { useWaterBodyLayers } from '../../../hooks/use-water-body-layers';
import LocationSelectorLayout from '../../../layouts/LocationSelectorLayout/LocationSelectorLayout';
import RadioChannelsMessageBlock from '../../../shared/RadioChannelsMessageBlock/RadioChannelsMessageBlock';
import { useRadioRanges } from '../hooks/useRadioRanges';

export interface MapLayer {
  layer: {
    id: string;
  };
  properties: {
    texttodisplay: string;
    areaWaterBodyKey: string;
    url?: string;
  };
}

let currentFeature: MapLayer;

const getCurrentWaterBodyFeature = (
  previousFeature: MapLayer,
): MapLayer | undefined => {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  if (!window?.deckeeMap) {
    return;
  }
  const x = window.innerWidth / 2;
  const y = window.innerHeight / 2;
  const boundingBox = [
    [x - 1, y - 1],
    [x + 1, y + 1],
  ];
  if (previousFeature) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    deckeeMap.setFeatureState(previousFeature, {
      currentUrl: null,
    });
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  currentFeature = window.deckeeMap
    .queryRenderedFeatures(boundingBox)
    .find(
      (i: MapLayer) =>
        i.layer.id === 'water-body-definitions fill' &&
        i.properties.areaWaterBodyKey,
    );

  if (currentFeature) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    deckeeMap.setFeatureState(currentFeature, {
      currentUrl: currentFeature.properties.areaWaterBodyKey,
    });
  }

  // eslint-disable-next-line
  // @ts-ignore
  // eslint-disable-next-line consistent-return
  return currentFeature;
};

const ChooseLocationPage: FC = () => {
  const history = useHistory();
  const handleBack = () => {
    history.goBack();
  };
  const dispatch = useDispatch();
  const currentMapCenter = useSelector(selectCurrentMapCenter);
  const canPrepareWa = useIsInBounds('WESTERNAUSTRALIA');
  const { radioChannels, isEnabled, isLoading } =
    useRadioRanges(currentMapCenter);

  const currentZoomLevel = useSelector(
    (state: AppState) =>
      state.appControls?.currentMapParams?.viewPortDetails?.zoom,
  );

  const selectedFeature = useMemo(() => {
    const hoveredFeature = getCurrentWaterBodyFeature(currentFeature);

    captureEvent({
      type: 'prepare: choose location move location',
      payload: {
        areaWaterBodyKey: hoveredFeature?.properties?.areaWaterBodyKey,
        currentMapCenter,
      },
    });
    return hoveredFeature;
  }, [currentMapCenter]);

  const map = window?.deckeeMap;

  useRadioRangeLayers(map);
  useWaterBodyLayers(map);

  useEffect(() => {
    dispatch(hideSearchButton(false));
    dispatch(hideAllLayers(true));
    return () => {
      dispatch(hideSearchButton(true));
      dispatch(hideAllLayers(false));
    };
  }, [dispatch]);

  const handleInitialState = React.useCallback(async () => {
    dispatch(
      await moveMapToLocation({
        currentMapCenter,
        pitch: 0,
        bearing: 0,
      }),
    );
    // don't include center as a dep as it will case the map to constantly move
    // to the users location as they move
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;

    if (isMounted) {
      handleInitialState();
      dispatch(showSelectMapPoint(-1)); // ;(
    }

    return () => {
      isMounted = false;
    };
  }, [dispatch, handleInitialState]);

  const handleSelectLocation = ({ lat, lon }: { lat: number; lon: number }) => {
    captureEvent({
      type: 'prepare: choose location',
      payload: {},
      sendToAmplitude: true,
    });

    if (!selectedFeature) {
      return;
    }

    dispatch(
      postMemberSettings({
        waterBodyDefinitionConfig: {
          latitude: lat,
          longitude: lon,
          intendedWatersName: selectedFeature?.properties?.texttodisplay,
          intendedAreaWaterBodyKey:
            selectedFeature?.properties?.areaWaterBodyKey,
        },
      }),
    );

    history.push('/maps/prepare/checklist');
  };
  return (
    <LocationSelectorLayout
      onBack={handleBack}
      onSelectLocation={handleSelectLocation}
      heading="Intended waters"
      instructionText="Move the pin to where you are going"
      buttonLabel="I'm going here"
      render={({ buttonLabel, handleOnComplete }) => (
        <Box
          px="3"
          mb="3"
          display="flex"
          flexDirection="column"
          alignItems="center"
        >
          <Paragraph pb="3" fontWeight="bold">
            {selectedFeature?.properties.texttodisplay ||
              (currentZoomLevel <= 8
                ? 'Zoom to accurately choose a water way!'
                : 'Move the pin onto water!')}
          </Paragraph>

          <Paragraph pb="3" textAlign="center">
            {currentMapCenter?.lat && currentMapCenter?.lon
              ? formatLatLon(
                  'decimal',
                  currentMapCenter.lat,
                  currentMapCenter.lon,
                )
              : ''}
          </Paragraph>

          {isEnabled && canPrepareWa && (
            <Box width="100%">
              <RadioChannelsMessageBlock
                isLoading={isLoading}
                radioChannels={radioChannels}
              />
            </Box>
          )}

          <Button
            wide
            mt={3}
            disabled={!selectedFeature}
            onClick={handleOnComplete}
          >
            {buttonLabel}
          </Button>
        </Box>
      )}
    />
  );
};

export default ChooseLocationPage;
